/**
 * API client for the pizza bot backend.
 * All requests include the JWT token from localStorage.
 */

const API_BASE = import.meta.env.VITE_API_URL || '/api';

interface ApiResponse<T> {
  data?: T;
  error?: string;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<ApiResponse<T>> {
  const token = localStorage.getItem('token');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const res = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    const data = await res.json();

    if (!res.ok) {
      return { error: data.error || `Request failed (${res.status})` };
    }

    return { data };
  } catch (err) {
    return { error: err instanceof Error ? err.message : 'Network error' };
  }
}

// --- Auth ---

export interface PickupPreference {
  mode: 'anytime' | 'specific';
  preferredTime?: string;
  toleranceBefore?: number;
  toleranceAfter?: number;
}

export interface User {
  userId: string;
  email: string;
  displayName: string;
  phone?: string;
  pizzaPreference?: string;
  quantity?: number;
  pickupWindow?: string | PickupPreference;
  isAdmin: boolean;
}

export interface LoginResponse {
  token: string;
  user: User;
}

export function login(email: string, password: string) {
  return request<LoginResponse>('POST', '/auth/login', { email, password });
}

export function register(inviteCode: string, email: string, password: string, displayName: string) {
  return request<User>('POST', '/auth/register', { inviteCode, email, password, displayName });
}

export function getMe() {
  return request<User>('GET', '/auth/me');
}

// --- User ---

export interface UserUpdate {
  phone?: string;
  pizzaPreference?: string;
  quantity?: number;
  pickupWindow?: string | PickupPreference;
  displayName?: string;
}

export function updateProfile(updates: UserUpdate) {
  return request<User>('PUT', '/users/me', updates);
}

// --- Sessions ---

export interface Session {
  sessionToken: string;
  userId: string;
  dropDate: string;
  status: string;
  smsCode?: string;
  checkoutUrl?: string;
  orderNumber?: string;
  error?: string;
  updatedAt: string;
}

export function getSession(token: string) {
  return request<Session>('GET', `/session/${token}`);
}

export function submitSmsCode(token: string, code: string) {
  return request<{ success: boolean }>('POST', `/session/${token}/sms`, { code });
}

export function submitPayment(
  token: string,
  cardNumber: string,
  cardExpiry: string,
  cardCvc: string,
  cardZip?: string,
) {
  return request<{ success: boolean }>('POST', `/session/${token}/payment`, {
    cardNumber, cardExpiry, cardCvc, cardZip,
  });
}

// --- Drops ---

export interface DropInfo {
  dropDate: string;
  dayOfWeek: string;
  isOptedIn: boolean;
  sessionToken?: string;
}

export function getNextDrop() {
  return request<DropInfo>('GET', '/drops/next');
}

export function getUpcomingDrops() {
  return request<{ drops: DropInfo[] }>('GET', '/drops/upcoming');
}

export function optIn(dropDate?: string) {
  return request<{ sessionToken: string; dropDate: string }>('POST', '/drops/opt-in', dropDate ? { dropDate } : undefined);
}

export function getMySessions() {
  return request<{ sessions: Session[] }>('GET', '/drops/my-sessions');
}

export function cancelSession(token: string) {
  return request<{ success: boolean }>('DELETE', `/drops/sessions/${token}`);
}

// --- Config ---

export interface MenuItem {
  name: string;
  price: number;
}

export function getMenu() {
  return request<{ items: MenuItem[] }>('GET', '/config/menu');
}

export function getPickupTimes() {
  return request<{ slots: string[] }>('GET', '/config/pickup-times');
}

// --- Admin ---

export function generateInviteCodes(count: number = 1) {
  return request<{ codes: string[] }>('POST', '/admin/invite-codes', { count });
}

export function listUsers() {
  return request<User[]>('GET', '/admin/users');
}
